import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pedidoApi } from '../services/api';
import {
  PageHeader, Loading, EmptyState,
  BadgeSituacao, BadgePagamento, BadgePrazo,
} from '../components/ui';
import { formatarMoeda, formatarData, formatarTelefone } from '../lib/format';
import { Search, PackagePlus, Printer, Eye, ShoppingBag, ChevronLeft, ChevronRight, Store } from 'lucide-react';

const situacoes = [
  { value: '', label: 'Todas as situações' },
  { value: 'AGUARDANDO_ENTRADA', label: 'Aguardando Entrada' },
  { value: 'EM_PRODUCAO', label: 'Em Produção' },
  { value: 'PRONTO', label: 'Pronto' },
  { value: 'ENTREGUE', label: 'Entregue' },
  { value: 'CANCELADO', label: 'Cancelado' },
];

export function PedidosPage() {
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['pedidos', busca, situacao, page],
    queryFn: () => pedidoApi.listar({ busca, situacao, page, limit: 20 }).then(r => r.data),
  });

  const vazio = !isLoading && (data?.data?.length || 0) === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Pedidos"
        descricao={`${data?.total ?? 0} pedido${(data?.total ?? 0) === 1 ? '' : 's'} cadastrado${(data?.total ?? 0) === 1 ? '' : 's'}`}
      >
        <Link to="/pedidos/novo" className="btn-primary">
          <PackagePlus className="w-4 h-4" />
          Novo Pedido
        </Link>
      </PageHeader>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar por cliente ou telefone..."
            value={busca}
            onChange={e => { setBusca(e.target.value); setPage(1); }}
            className="input-field !pl-10"
          />
        </div>
        <select
          value={situacao}
          onChange={e => { setSituacao(e.target.value); setPage(1); }}
          className="input-field w-full sm:w-56"
        >
          {situacoes.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Tabela */}
      <div className="table-shell">
        {isLoading ? (
          <Loading />
        ) : vazio ? (
          <EmptyState
            icon={ShoppingBag}
            titulo="Nenhum pedido encontrado"
            descricao="Ajuste os filtros ou cadastre um novo pedido."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Cliente</th>
                    <th>Valor Total</th>
                    <th>Prazo</th>
                    <th>Situação</th>
                    <th>Pagamento</th>
                    <th className="!text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.map((p: any) => (
                    <tr key={p.id}>
                      <td className="font-display font-semibold text-ink-900">
                        #{p.numero}
                        {p.origem === 'LOJA' && (
                          <span className="ml-2 badge bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/20">
                            <Store className="w-3 h-3" /> Loja
                          </span>
                        )}
                      </td>
                      <td>
                        <p className="font-medium text-ink-800">{p.cliente?.nome}</p>
                        <p className="text-xs text-ink-400 mt-0.5">{formatarTelefone(p.cliente?.telefone)}</p>
                      </td>
                      <td className="font-semibold text-ink-900">{formatarMoeda(p.valorTotal)}</td>
                      <td>
                        <p className="text-ink-700">{formatarData(p.prazo)}</p>
                        <div className="mt-1"><BadgePrazo tipo={p.tipoPrazo} /></div>
                      </td>
                      <td><BadgeSituacao situacao={p.situacao} /></td>
                      <td><BadgePagamento situacao={p.situacaoPagamento} /></td>
                      <td>
                        <div className="flex items-center justify-center gap-1">
                          <button className="icon-btn" title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="icon-btn" title="Imprimir">
                            <Printer className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {data?.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-ink-100">
                <p className="text-[13px] text-ink-500">
                  Página <span className="font-semibold text-ink-800">{page}</span> de {data.totalPages}
                  <span className="text-ink-300"> · </span>
                  {data.total} pedidos
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn-secondary !px-3 !py-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                    disabled={page === data.totalPages}
                    className="btn-secondary !px-3 !py-2"
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
